import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  postQuotesQueryRequest,
  selectClient,
  selectMaybeClientPartner,
  selectQuotes,
  selectQuotesIsLoading,
} from '@client/model';
import { AppState } from '@client/model/store';
import { createQuoteWithROIandProjectedCoverage } from '@client/util/policyQuoteCalculations';
import { formatROI, FundingPolicyType } from '@shared';
import { calcClientDemographics } from '@shared/calcClientDemographics';
import { fundingPolicyTypeDefs } from '@shared/FundingPolicyTypeDef';

interface UseLtcPlanningQuotesOptions {
  enabled?: boolean;
}

/**
 * Determine the policy type based on selected policy types
 * - Joint Life: if any joint policies are selected
 * - Couples Discount: if Securian Financial or Lincoln Financial policies are selected
 * - Single Life: default
 */
function getPolicyTypeFromSelectedPolicies(selectedPolicyTypes: FundingPolicyType[]): string {
  const jointPolicies = new Set([
    FundingPolicyType.jointAssetCare,
    FundingPolicyType.jointAssetcareThree,
    FundingPolicyType.jointCareMatters,
    FundingPolicyType.jointLongTermCareInsurance,
    FundingPolicyType.jointThriventLongTermCareInsurance,
    FundingPolicyType.jointHybridAssetBased,
    FundingPolicyType.jointHybridAnnuityBased,
    FundingPolicyType.jointHybridLifeIra,
    FundingPolicyType.nylJointSecureCare,
    FundingPolicyType.jointForecareFixedAnnuity,
    FundingPolicyType.jointNglEssentialLtc,
    FundingPolicyType.jointMutualOfOmahaMutualcare,
  ]);

  const hasJointPolicy = selectedPolicyTypes.some(type => jointPolicies.has(type));

  if (hasJointPolicy) {
    return 'Joint Life';
  }

  const couplesDiscountPolicies = new Set([
    FundingPolicyType.secureCareThree,
    FundingPolicyType.moneyGuardFixedAdvantage,
  ]);

  const hasCouplesDiscountPolicy = selectedPolicyTypes.some(type => couplesDiscountPolicies.has(type));

  if (hasCouplesDiscountPolicy) {
    return 'Couples Discount';
  }

  return 'Single Life';
}

export function useLtcPlanningQuotes(options: UseLtcPlanningQuotesOptions = {}) {
  const { enabled = true  } = options;

  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const clientPartner = useSelector(selectMaybeClientPartner);
  const allQuotes = useSelector(selectQuotes);
  const isLoading = useSelector(selectQuotesIsLoading);

  const { selectedPolicyTypes } = useSelector(
    (state: { ltcFinancialPlanning: AppState['ltcFinancialPlanning'] }) =>
      state.ltcFinancialPlanning,
  );

  // Fetch quotes on mount if enabled
  useEffect(() => {
    if (!enabled || !client?.intakeSurvey || selectedPolicyTypes.length === 0) {
      return;
    }

    const demographics = calcClientDemographics(client.intakeSurvey);
    const policyType = getPolicyTypeFromSelectedPolicies(selectedPolicyTypes);

    const demographicsData: FetchQuotesDemographics = {
      primaryAge: demographics.primaryAge,
      primaryGender: demographics.primaryGender,
      primaryTobaccoStatus: demographics.primaryTobaccoStatus,
      usaState: demographics.state?.stateAbbreviation ?? '',
      policyType,
      maritalStatus: client.intakeSurvey.clientMaritalStatus,
    };

    // Include partner demographics for joint policies if partner exists
    if (policyType === 'Joint Life' && clientPartner?.intakeSurvey) {
      const partnerDemographics = calcClientDemographics(clientPartner.intakeSurvey);
      demographicsData.secondaryAge = partnerDemographics.primaryAge;
      demographicsData.secondaryGender = partnerDemographics.primaryGender;
      demographicsData.secondaryTobaccoStatus = partnerDemographics.primaryTobaccoStatus;
    }

    dispatch(
      postQuotesQueryRequest({ demographics: demographicsData, advisorId: client.advisorId }),
    );
  }, [enabled, client, clientPartner, dispatch, selectedPolicyTypes]);

  // Filter quotes by selected policy type categories using calculationType
  const filteredQuotes = useMemo(() => {
    if (!allQuotes?.length || selectedPolicyTypes.length === 0) {
      return [];
    }

    return allQuotes.filter(quote => {
      const policyDef = fundingPolicyTypeDefs[quote.fundingPolicyType];
      if (!policyDef) return false;

      // Check if this quote's calculationType matches any selected category
      return selectedPolicyTypes.includes(policyDef.calculationType);
    });
  }, [allQuotes, selectedPolicyTypes]);

  // Calculate ROI and sort quotes
  const quotesWithROI = useMemo(() => {
    if (!filteredQuotes.length || !client) {
      return [];
    }

    const processed = filteredQuotes
      .map(quote => {
        try {
          const enriched = createQuoteWithROIandProjectedCoverage(
            quote,
            client,
            'total',
          );

          const formattedROI = formatROI(
            enriched.roiValues,
            'total',
          );

          return {
            ...enriched,
            formattedROI,
          };
        } catch (error) {
          console.error('Error calculating ROI for quote:', quote.insuranceQuoteId, error);
          return null;
        }
      })
      .filter((q): q is NonNullable<typeof q> => q !== null)
      .filter(q => (q.projectedCostCoverage ?? 0) > 0)
      .sort((a, b) => (b.roi ?? 0) - (a.roi ?? 0));

    return processed;
  }, [filteredQuotes, client]);

  // Get policy type label for a quote
  const getPolicyTypeLabel = (fundingPolicyType: FundingPolicyType): string => {
    const policyDef = fundingPolicyTypeDefs[fundingPolicyType];
    return policyDef?.label ?? 'Unknown Policy Type';
  };

  return {
    allQuotes: quotesWithROI,
    isLoading,
    totalCount: quotesWithROI.length,
    selectedPolicyTypes,
    getPolicyTypeLabel,
  };
}

